import { useState, ChangeEvent, useContext } from 'react';
import { Modal, ModalPosition } from 'common/modal';
import { Input } from 'common/input';
import { Button, ButtonSize, ButtonStyle } from 'common/button';
import { Spinner } from 'common/spinner';
import { UserInitials } from 'common/user-initials';
import {
  classnames,
  isEmailValid,
  getInitials,
  getQueryParam,
} from 'helpers/utils';
import { goToPage, RouteName } from 'routes';
import { AppContext, appActions } from 'context/app-context';
import { NotificationObject } from 'models/notificationObject';
import { NotificationType } from 'common/enums';

type GuestRegistrationModalProps = {
  invitationText: string;
  successMessage: string;
  onRegister: (params: {
    email: string;
    name: string;
    token: string;
  }) => Promise<{
    token: string;
    vaultId: number;
    albumId?: number;
    albumUrl?: string;
  }>;
};

export const GuestRegistrationModal = ({
  invitationText,
  successMessage,
  onRegister,
}: GuestRegistrationModalProps) => {
  const { dispatch } = useContext(AppContext);
  const token = getQueryParam('token');
  const inviter = getQueryParam('inviter');
  const [show, setShow] = useState(!!token);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [nameError, setNameError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [registering, setRegistering] = useState(false);
  // Base64 of the UTF-8 name (accented names included).
  const inviterName = (() => {
    if (!inviter) return '';
    try {
      return decodeURIComponent(escape(atob(inviter)));
    } catch {
      return '';
    }
  })();

  const validateForm = () => {
    let isValid = true;
    setNameError('');
    setEmailError('');

    if (!name.trim()) {
      setNameError('Name is required');
      isValid = false;
    }

    if (!email.trim()) {
      setEmailError('Email is required');
      isValid = false;
    } else if (!isEmailValid(email)) {
      setEmailError('Please enter a valid email address');
      isValid = false;
    }

    return isValid;
  };

  const handleRegister = async () => {
    if (!validateForm()) {
      return;
    }

    if (!token) {
      return;
    }

    try {
      setRegistering(true);
      const registeredGuestToken = await onRegister({
        email: email.trim(),
        name: name.trim(),
        token,
      });

      setShow(false);

      dispatch({
        type: appActions.NOTIFICATION,
        notification: new NotificationObject({
          show: true,
          title: 'Welcome!',
          message: successMessage,
          type: NotificationType.Success,
        }),
      });

      if (registeredGuestToken.albumId) {
        goToPage(RouteName.AlbumShareGuest, {
          guestToken: registeredGuestToken.token,
          vaultId: registeredGuestToken.vaultId,
          albumId: registeredGuestToken.albumId,
        });
      } else {
        goToPage(RouteName.SelfRecordingGuest, {
          guestToken: registeredGuestToken.token,
          vaultId: registeredGuestToken.vaultId,
        });
      }
    } catch (error: any) {
      if (error.status === 404) {
        dispatch({
          type: appActions.NOTIFICATION,
          notification: new NotificationObject({
            show: true,
            title: 'Invalid link',
            message:
              'This link is invalid or has expired, please request another one',
            type: NotificationType.Error,
          }),
        });
        return;
      }
      dispatch({
        type: appActions.NOTIFICATION,
        notification: new NotificationObject({
          show: true,
          title: 'Registration failed',
          message: 'Please try again',
          type: NotificationType.Error,
        }),
      });
    } finally {
      setRegistering(false);
    }
  };

  const handleNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    if (nameError) setNameError('');
  };

  const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (emailError) setEmailError('');
  };

  if (!show) return null;

  return (
    <Modal
      position={ModalPosition.Center}
      disableManualClosing
      contentClassName="py-8 px-14 !w-[500px] !h-[642px] flex flex-col !gap-0"
    >
      <div className="flex h-full flex-col items-center gap-4">
        <div className="flex justify-center">
          <UserInitials name={getInitials(inviterName)} />
        </div>
        <p
          className={classnames(
            'text__body__regular__medium__gray900',
            'px-6 text-center',
          )}
        >
          <span className="text__body__semi__bold__medium__gray900">
            {inviterName}
          </span>{' '}
          {invitationText}
        </p>
        <Input
          id="name"
          name="name"
          label="Name"
          placeholder="Your name"
          value={name}
          type="text"
          errorMessage={nameError}
          onChange={handleNameChange}
          className="w-full"
        />
        <Input
          id="email"
          name="email"
          label="Email"
          placeholder="your.email@example.com"
          value={email}
          type="email"
          errorMessage={emailError}
          onChange={handleEmailChange}
          className="w-full"
        />
        <Button
          buttonStyle={ButtonStyle.Primary}
          buttonSize={ButtonSize.Large}
          onClick={handleRegister}
          disabled={registering || !name.trim() || !email.trim()}
          className="w-full"
        >
          {registering ? (
            <Spinner color="white" className="spinner-inside-button" />
          ) : (
            'Contribute as guest'
          )}
        </Button>
        <div className="flex w-full items-center justify-center">
          <span className="text__body__regular__small__gray500">or</span>
        </div>
        <Button
          buttonStyle={ButtonStyle.SecondaryGray}
          buttonSize={ButtonSize.Large}
          onClick={() => goToPage(RouteName.SignUp)}
          className="w-full border border-gray-200"
        >
          Create an account
        </Button>
        <div className="flex items-center justify-center gap-1">
          <span className="text__body__regular__small__gray700">
            Already have an account?{' '}
          </span>
          <Button
            buttonStyle={ButtonStyle.LinkColor}
            buttonSize={ButtonSize.Small}
            onClick={() => goToPage(RouteName.SignIn)}
            className="p-0! underline"
          >
            Log in
          </Button>
        </div>
        <p className="text__body__regular__small__gray500">
          By continuing, you agree to our Privacy Policy.
        </p>
      </div>
    </Modal>
  );
};
